import { useEffect, useRef, useState } from 'react';

/**
 * Reveals an element once it scrolls into the viewport.
 * Returns a ref to attach and a boolean that flips true on first intersection.
 */
function useInView(options = { threshold: 0.15 }) {
  const elementRef = useRef(null);
  const [hasEntered, setHasEntered] = useState(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) {
      return undefined;
    }

    const prefersReducedMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Reveal immediately when motion is unwanted or the API is unavailable
    // (e.g. reduced-motion users, older browsers, or the test environment).
    if (prefersReducedMotion || typeof IntersectionObserver === 'undefined') {
      setHasEntered(true);
      return undefined;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setHasEntered(true);
        observer.disconnect();
      }
    }, options);

    observer.observe(element);
    return () => observer.disconnect();
  }, [options]);

  return [elementRef, hasEntered];
}

export default useInView;
